import { useQueries, useQuery } from "@tanstack/react-query";
import {
  communities,
  courses,
  districts,
  incomes,
  interests,
  listValues,
  countriescode,
  nationality,
  personalities,
  profession,
  qualification,
  religions,
  states,
  UserCommunities,
} from "api/listing";
import { towns } from "./../../api/listing";
import { mapToSelectOptions } from "utils/formatHelper";

const listValueTypes = {
  maritalStatus: "marital_status",
  familyStatus: "family_status",
  bodyType: "body_type",
  complexion: "complexion",
  diet: "diet",
  bloodGroup: "blood_group",
  jobSector: "job_sector",
  physicalStatus: "physical_status",
  smokeStatus: "smoke_status",
  drinkStatus: "drink_status",
  familyType: "family_type",
  userStatus: "user_status",
  messageStatus: "message_status",
  messageType: "message_type",
  messageTypeDeleted: "message_type_deleted",
  albumVisibility: "album_visibility",
  profileCreatedBy: "profile_created_by",
  motherTongue: "mother_tongue",
  contactPerson: "contact_person",
  gender: "gender",
  community: "community",
  hobby: "hobby",
  drink: "drink",
  smoke: "smoke",
  profileCreatedFor: "profile_created_for",
};

const apiValueTypes = {
  religion: { fn: religions, key: "religions" },
  nationality: { fn: nationality, key: "countries" },
  qualification: { fn: qualification, key: "qualification" },
};

export const useSelectOptions = ({ screenTypes = [] } = {}) => {
  const listQueries = useQueries({
    queries: Object.entries(listValueTypes).map(([key, type]) => {
      const hasScreen = screenTypes.includes(key);
      const log = {
        queryKey: ["listValues", type, hasScreen],
        queryFn: () => listValues("", type, hasScreen ? 1 : undefined),
        enabled: !!type,
      };
      return {
        queryKey: ["listValues", type, hasScreen],
        queryFn: () => listValues("", type, hasScreen ? 1 : undefined),
        enabled: !!type,
      };
    }),
  });

  const apiQueries = useQueries({
    queries: Object.entries(apiValueTypes).map(([key, { fn, key: qk }]) => {
      const hasScreen = screenTypes.includes(key);
      return {
        queryKey: [qk, 1, hasScreen],
        queryFn: () => fn("", 1, hasScreen ? 1 : undefined),
        enabled: true,
      };
    }),
  });

  const mapResults = (keys, queries) =>
    keys.reduce((acc, key, idx) => {
      const result = queries[idx] || {};
      acc[key] = {
        options: mapToSelectOptions(result.data),
        isLoading: result.isPending,
        isError: result.isError,
      };
      return acc;
    }, {});

  const listMapped = mapResults(Object.keys(listValueTypes), listQueries);
  const apiMapped = mapResults(Object.keys(apiValueTypes), apiQueries);

  return {
    ...listMapped,
    ...apiMapped,
  };
};

export const useCommunities = ({ search = "", parent_id, screen }) => {
  return useQuery({
    queryKey: ["communities", parent_id, search, screen],
    queryFn: () => communities(search, parent_id, screen),
    enabled: !!parent_id,
  });
};

export const useStates = ({ search = "", country_id }) => {
  return useQuery({
    queryKey: ["states", country_id, search],
    queryFn: () => states(search, country_id),
    enabled: !!country_id,
  });
};
export const useSelectCode = ({ search_key }) => {
  return useQuery({
    queryKey: ["countries-code", search_key],
    queryFn: () => countriescode(search_key),
  });
};

export const useDistrict = ({ search = "", parent_id }) => {
  return useQuery({
    queryKey: ["districts", parent_id, search],
    queryFn: () => districts(search, parent_id),
    enabled: !!parent_id,
  });
};

export const useTown = ({ search = "", place_id }) => {
  return useQuery({
    queryKey: ["towns", place_id, search],
    queryFn: () => towns(search, place_id),
    enabled: !!place_id,
  });
};

export const useInterests = () => {
  return useQuery({
    queryKey: ["interests"],
    queryFn: interests,
  });
};

export const usePersonalities = () => {
  return useQuery({
    queryKey: ["personalities"],
    queryFn: personalities,
  });
};

export const useCourses = ({ search = "", parent_id = "" }) => {
  return useQuery({
    queryKey: ["courses", search, parent_id],
    queryFn: () => courses(search, parent_id),
    enabled: Array.isArray(parent_id) ? parent_id.length > 0 : !!parent_id,
  });
};

export const useProfession = ({ search = "", screen }) => {
  return useQuery({
    queryKey: ["profession", search, screen],
    queryFn: () => profession(search, screen),
  });
};

export const useIncomes = ({ search = "", screen }) => {
  return useQuery({
    queryKey: ["incomes", search, screen],
    queryFn: () => incomes(search, screen),
  });
};

export const useUserCommunities = ({ search = "" }) => {
  return useQuery({
    queryKey: ["user_community", search],
    queryFn: () => UserCommunities(search),
  });
};
